import { List, ListItem, ListItemButton, ListItemIcon, ListItemText, useTheme } from '@mui/material';
import { FC } from 'react';
import { toast } from 'react-toastify';
import { DocumentDetails, PolicyDocumentType, DocumentsClient } from '@acorn-portal/shared/src/services/api';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { downloadPdf } from '@acorn-portal/shared/src/utils/download-pdf';

interface IDocument {
	documentType: PolicyDocumentType;
	description: string;
}

interface IDocumentUrl extends IDocument {
	url: string;
}

export type DocumentItem = DocumentDetails | IDocumentUrl;

export const isDocumentUrl = (item: DocumentItem): item is IDocumentUrl => {
	return typeof (item as IDocumentUrl).url === 'string';
};

interface IOpenDocumentButtonProps {
	item: DocumentItem;
	setDownloading: (downloading: boolean) => void;
}

const OpenDocumentButton: FC<IOpenDocumentButtonProps> = ({ item, setDownloading }) => {
	const themeUtils = useTheme();

	const downloadDocument = async (url: string, documentName: string) => {
		try {
			setDownloading(true);
			const client = new DocumentsClient(undefined, API);
			const policyDocument = await client.getDocumentContent(url);
			downloadPdf(policyDocument, documentName);
			setDownloading(false);
		} catch (er) {
			console.error('Error downloading document:', er);
			setDownloading(false);
			toast('Unable download your document Please try again.', {
				position: 'top-right',
				autoClose: 3000,
				pauseOnHover: true,
				style: {
					background: themeUtils.palette.primary.main,
					color: themeUtils.palette.common.white,
				},
			});
		}
	};

	let buttonProps;

	if (isDocumentUrl(item)) {
		buttonProps = {
			href: item.url,
			target: '_blank',
			rel: 'noreferrer',
		};
	} else {
		buttonProps = {
			onClick: () => downloadDocument(item.id, item.description),
		};
	}

	return (
		<List sx={{ '&:first-of-type': { pt: 0 } }}>
			<ListItem disablePadding>
				<ListItemButton
					sx={(theme) => ({
						border: '0.3px solid #E6E6E6',
						borderRadius: '3px',
						overflow: 'hidden',
						'&:hover': {
							border: `0.3px solid ${theme.palette.primary.main}`,
							'& span': { fontWeight: 500 },
						},
					})}
					{...buttonProps}
				>
					<ListItemIcon>
						<PictureAsPdfIcon
							sx={(theme) => ({
								color: theme.palette.primary.main,
							})}
						/>
					</ListItemIcon>
					<ListItemText primary={item.description} />
				</ListItemButton>
			</ListItem>
		</List>
	);
};

export default OpenDocumentButton;
